/**
 * L'entrée « Messages » de la barre de navigation, et son badge de non-lus.
 *
 * Jumelle de `NotificationBell`, à une différence près : elle **n'ouvre pas** le
 * flux SSE. Un seul flux par onglet suffit — c'est le même canal (docs/13 §5),
 * et l'ouvrir deux fois doublerait les connexions tenues par le serveur pour
 * livrer exactement les mêmes trames.
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { useUnreadMessageCount } from "@/features/messaging/api/useMessaging";

export function MessagesLink() {
  const { t } = useTranslation("messaging");
  const { data: unread } = useUnreadMessageCount();
  const count = unread ?? 0;

  return (
    <Link
      to="/messages"
      className="relative inline-flex items-center rounded-md p-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
      aria-label={t("nav.label")}
      data-testid="messages-link"
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="h-5 w-5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H9l-5 4v-4H6.5"
        />
      </svg>
      {count > 0 ? (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-red-600 px-1 text-center text-[10px] leading-4 font-semibold text-white"
          data-testid="messages-badge"
        >
          {/* Plafonné à l'affichage, jamais dans le compteur lui-même. */}
          {count > 99 ? "99+" : count}
        </span>
      ) : null}
      <span className="sr-only">{t("nav.unread", { unreadValue: count })}</span>
    </Link>
  );
}
